import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Other devices have been signed out.");
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Change password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-2">
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" type="password" value={current} required autoComplete="current-password"
              onChange={(e) => setCurrent(e.target.value.replace(/\s/g, ""))} data-testid="cp-current" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-new">New password</Label>
            <Input id="cp-new" type="password" value={next} required minLength={8} autoComplete="new-password"
              onChange={(e) => setNext(e.target.value.replace(/\s/g, ""))} placeholder="At least 8 characters" data-testid="cp-new" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input id="cp-confirm" type="password" value={confirm} required minLength={8} autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value.replace(/\s/g, ""))} data-testid="cp-confirm" />
          </div>
          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="cp-error">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="cp-submit"
              className="bg-navy text-white hover:bg-navy-hover">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Change password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
